import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import SpinWheel from "../components/SpinWheel";
import { useAuth } from "../contexts/AuthContext";
import { Gift, MessageCircle, AlertTriangle, ArrowLeft, UserPlus } from "lucide-react";

export default function LykkehjulVinst() {
  const { spinId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [spinningTo, setSpinningTo] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/wheel-spins/${spinId}`)
      .then((r) => {
        setData(r.data);
        // Start animasjon etter en kort delay slik at hjulet rendrer i nullstilling først
        setTimeout(() => setSpinningTo(r.data.spin.segment_index), 400);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Kunne ikke hente gevinst");
      })
      .finally(() => setLoading(false));
  }, [spinId]);

  if (loading) {
    return <Layout><div className="text-center text-[var(--cream-soft)] py-20" data-testid="vinst-loading">Laster gevinst…</div></Layout>;
  }
  if (error) {
    return (
      <Layout>
        <div className="panel-tight p-12 text-center max-w-xl mx-auto" data-testid="vinst-error">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Ikke tilgjengelig</h1>
          <p className="text-[var(--cream-soft)] text-sm mb-6">{error}</p>
          <Link to="/" className="text-[var(--magenta)] hover:underline">← Tilbake til forsiden</Link>
        </div>
      </Layout>
    );
  }
  if (!data) return null;

  const { spin, wheel, creator, winner, is_winner, is_creator, is_anonymous } = data;
  const spunAt = new Date(spin.created_at).toLocaleString("nb-NO", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
  });

  // Anonyme vinnere har ingen "innlogget vinner-rolle". Vi viser dem vinner-perspektivet
  // siden de ankom via URLen (hjulet's hemmelighet) og MÅ se claim-ID + kontaktinfo.
  const showWinnerView = is_winner || (is_anonymous && !is_creator);
  const showSignupCta = is_anonymous && !user;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="vinst-page">
        <Link to={user ? "/meldinger" : "/"} className="text-xs font-meta text-[var(--cream-soft)] hover:text-[var(--magenta)] flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> {user ? "Tilbake til innboks" : "Til forsiden"}
        </Link>

        {/* Hero med hjul + premie */}
        <div className="panel p-8 mb-8 text-center">
          <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-2">
            {is_creator ? "NY VINNER" : "DIN VINNST"}
          </div>
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-1" data-testid="vinst-title">
            «{spin.segment_label}»
          </h1>
          <div className="text-sm text-[var(--cream-soft)] mb-6">
            på <span className="text-[var(--magenta)]">{wheel.title}</span>
          </div>

          {/* Hjulet – animeres ved første render, fryser på vinnersegmentet */}
          <div className="flex justify-center mb-6" data-testid="vinst-wheel">
            <SpinWheel
              segments={wheel.segments}
              spinningTo={spinningTo}
              size={340}
              theme={{ accent: "#D11A5B" }}
            />
          </div>

          <div className="text-xs font-meta tracking-[0.15em] text-[var(--cream-faint)]">
            SPUNNET {spunAt}
          </div>
        </div>

        {/* For vinneren (innlogget ELLER anonym) */}
        {showWinnerView && (
          <div className="space-y-5">
            {/* Claim-ID-kort – vises for ALLE vinnere (innlogget og anonym).
                 Kreator verifiserer denne på ekstern plattform før gevinst leveres. */}
            {spin.claim_id && (
              <div className="panel p-6 border-l-2 border-[var(--magenta)]" data-testid="vinst-claim-id-card">
                <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">
                  DIN CLAIM-ID
                </div>
                <div className="font-mono text-2xl text-[var(--cream)] tracking-widest mb-2" data-testid="vinst-claim-id">
                  {spin.claim_id}
                </div>
                <p className="text-xs text-[var(--cream-soft)] leading-relaxed">
                  Vis denne ID-en til {is_anonymous ? (
                    <>kreatoren på <strong className="text-[var(--cream)]">{spin.external_platform || "plattformen"}</strong> der du kjøpte koden</>
                  ) : (
                    <>kreatoren på den eksterne siden der du kjøpte koden</>
                  )}.
                  De bruker den for å bekrefte at det er deg som vant.
                </p>
              </div>
            )}

            <div className="panel p-6" data-testid="vinst-creator-info">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 shrink-0 border-2 border-[var(--magenta)] overflow-hidden bg-[var(--noir-2)]">
                  {creator.creator_avatar_url ? (
                    <img src={creator.creator_avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-fraunces italic text-lg text-[var(--cream-soft)]">
                      {(creator.username || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1 flex items-center gap-2">
                    <Gift className="h-3 w-3 text-[var(--magenta)]" /> SKAPEREN TAR KONTAKT
                  </div>
                  <p className="text-sm text-[var(--cream)] leading-relaxed">
                    <span className="text-[var(--magenta)]">@{creator.username}</span> har fått varsel om gevinsten din.
                    De tar kontakt på {is_anonymous ? (
                      <strong className="text-[var(--cream)]">{spin.external_platform || "plattformen"}</strong>
                    ) : (
                      <>den eksterne siden (OnlyFans, Snapchat eller lignende)</>
                    )} hvor du kjøpte
                    kode <span className="font-mono bg-[var(--noir-2)] px-2 py-0.5 text-[var(--magenta)]">{spin.code}</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* Signup-CTA – kun for anonyme uten konto */}
            {showSignupCta && (
              <div className="panel p-6 border border-[var(--magenta)]/40 bg-[var(--magenta)]/5" data-testid="vinst-signup-cta">
                <div className="flex items-start gap-4">
                  <UserPlus className="h-5 w-5 text-[var(--magenta)] mt-1 shrink-0" />
                  <div className="flex-1">
                    <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">
                      LAG EN GRATIS KONTO
                    </div>
                    <p className="text-sm text-[var(--cream)] leading-relaxed mb-3">
                      Få oversikt over gevinstene dine og følg favoritt-skaperne dine på Norges beste erotiske community.
                    </p>
                    <Link
                      to="/?tab=register"
                      data-testid="vinst-signup-btn"
                      className="inline-flex items-center gap-2 bg-[var(--magenta)] text-white px-4 py-2 text-xs font-meta tracking-[0.15em] hover:opacity-90"
                    >
                      <UserPlus className="h-4 w-4" /> LAG KONTO
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className="panel p-6 border-l-2 border-[var(--magenta)]" data-testid="vinst-admin-fallback">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[var(--magenta)] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">
                    FÅR DU IKKE DET SOM ER LOVET?
                  </div>
                  <p className="text-sm text-[var(--cream-soft)] leading-relaxed mb-3">
                    Hvis skaperen ikke leverer premien innen rimelig tid, eller noe annet er galt med
                    gevinsten — gi beskjed til Norsk Erotikks admin så følger vi opp.
                    {is_anonymous && !user && " (Du må være innlogget for å sende melding.)"}
                  </p>
                  <Link
                    to={user ? "/meldinger?til=Admin" : "/?tab=register"}
                    data-testid="vinst-contact-admin-btn"
                    className="inline-flex items-center gap-2 bg-[var(--magenta)] text-white px-4 py-2 text-xs font-meta tracking-[0.15em] hover:opacity-90"
                  >
                    <MessageCircle className="h-4 w-4" /> {user ? "KONTAKT ADMIN" : "LAG KONTO FOR Å KONTAKTE"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* For skaperen */}
        {is_creator && (
          <div className="panel p-6" data-testid="vinst-creator-view">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-3">
              VINNER-DETALJER
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-[var(--cream-soft)]">Brukernavn på Norsk Erotikk</span>
                <Link
                  to={`/profile/${winner?.username || ""}`}
                  className="text-[var(--magenta)] font-medium hover:underline"
                  data-testid="vinst-winner-username"
                >
                  @{winner?.username || "ukjent"}
                </Link>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-[var(--cream-soft)]">Kode brukt</span>
                <span className="text-[var(--magenta)] font-mono">{spin.code}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-[var(--cream-soft)]">Premie</span>
                <span className="text-[var(--cream)]">{spin.segment_label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--cream-soft)]">Tidspunkt</span>
                <span className="text-[var(--cream)]">{spunAt}</span>
              </div>
            </div>
            <p className="mt-5 text-xs text-[var(--cream-faint)] leading-relaxed">
              Bekreftelses-DM er allerede sendt til vinneren i deres private innboks.
              Du finner full samtale-tråd i din skaper-innboks.
            </p>
            <Link
              to="/meldinger?context=creator"
              className="inline-flex items-center gap-2 mt-4 text-xs font-meta tracking-[0.15em] text-[var(--magenta)] hover:underline"
            >
              <MessageCircle className="h-3 w-3" /> ÅPNE SKAPER-INNBOKS →
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
